import type { UIMessage } from 'ai';

import { AgentApiRequestHelper } from './agent-api-request-helper';
import { AGENT_CONVERSATIONS_PATH } from './constants';
import type {
  AgentConversationCreatedView,
  AgentConversationEnvelope,
  AgentConversationFeedbackListEnvelope,
  AgentConversationListEnvelope,
  AgentConversationReadOptions,
  AgentConversationRenamedView,
  AgentConversationSendAcceptedView,
  AgentQueryValue,
  CommendaAiConversationVisibility,
  CommendaAiDocumentProcessingStage,
  CommendaAiFeedbackCategory,
  CommendaAiFeedbackRating,
  CommendaAiMessageFeedbackResponse,
  CommendaAiMessageMetadata,
  CommendaAiMutationResult,
  CreateAgentConversationBody,
  ListAgentConversationsQuery,
  SendAgentConversationMessageBody,
} from './types';

function withQuery(
  path: string,
  params: Record<string, AgentQueryValue>
): string {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) search.set(key, String(value));
  });

  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

const conversationPath = (conversationId: string, suffix = '') =>
  `${AGENT_CONVERSATIONS_PATH}/${conversationId}${suffix}`;

export async function readAgentConversation(
  conversationId: string,
  signal?: AbortSignal,
  options?: Readonly<AgentConversationReadOptions>
): Promise<AgentConversationEnvelope> {
  return AgentApiRequestHelper.read<AgentConversationEnvelope>(
    withQuery(conversationPath(conversationId), {
      runNumber: options?.runNumber,
      openedFrom: options?.openedFrom,
    }),
    signal
  );
}

export async function createAgentConversation(
  body: Readonly<CreateAgentConversationBody>
): Promise<CommendaAiMutationResult<AgentConversationCreatedView>> {
  return AgentApiRequestHelper.mutate<AgentConversationCreatedView>(
    AGENT_CONVERSATIONS_PATH,
    { method: 'POST', body },
    'Could not create the conversation.'
  );
}

export async function listAgentConversations(
  query: Readonly<ListAgentConversationsQuery> = {}
): Promise<AgentConversationListEnvelope> {
  return AgentApiRequestHelper.read<AgentConversationListEnvelope>(
    withQuery(AGENT_CONVERSATIONS_PATH, {
      profile: query.profile,
      companyId: query.companyId,
      scope: query.scope,
      excludeRunSessions: query.excludeRunSessions,
      workflowTemplateId: query.workflowTemplateId,
      limit: query.limit,
    })
  );
}

/** No workspace or scope argument here or on the three below: every one of these routes resolves
 * the conversation from its id and then authorizes the caller against it, including the
 * firm-membership re-check. Only the list takes a scope, because only the list has to filter. */
export async function deleteAgentConversation(
  conversationId: string
): Promise<CommendaAiMutationResult> {
  return AgentApiRequestHelper.mutate(
    conversationPath(conversationId),
    { method: 'DELETE' },
    'Could not delete the conversation.'
  );
}

export async function renameAgentConversation(
  conversationId: string,
  title: string
): Promise<CommendaAiMutationResult<AgentConversationRenamedView>> {
  return AgentApiRequestHelper.mutate<AgentConversationRenamedView>(
    conversationPath(conversationId),
    { method: 'PATCH', body: { title } },
    'Could not rename the conversation.'
  );
}

export async function listAgentConversationFeedback(
  conversationId: string
): Promise<AgentConversationFeedbackListEnvelope> {
  return AgentApiRequestHelper.read<AgentConversationFeedbackListEnvelope>(
    conversationPath(conversationId, '/feedback')
  );
}

export async function readAgentConversationDocumentProcessingStage(
  conversationId: string
): Promise<{ stage: CommendaAiDocumentProcessingStage }> {
  return AgentApiRequestHelper.read<{
    stage: CommendaAiDocumentProcessingStage;
  }>(conversationPath(conversationId, '/document-processing'));
}

export async function setAgentConversationVisibility(
  conversationId: string,
  visibility: CommendaAiConversationVisibility
): Promise<CommendaAiMutationResult> {
  return AgentApiRequestHelper.mutate(
    conversationPath(conversationId, '/visibility'),
    { method: 'PATCH', body: { visibility } },
    'Could not change who can see the conversation.'
  );
}

/** Keyed by message because that is what the transcript holds; the backend resolves the run. */
export async function submitAgentConversationMessageFeedback(
  conversationId: string,
  messageId: string,
  verdict: Readonly<{
    rating: CommendaAiFeedbackRating;
    category?: CommendaAiFeedbackCategory;
    comment?: string;
  }>
): Promise<CommendaAiMutationResult<CommendaAiMessageFeedbackResponse>> {
  return AgentApiRequestHelper.mutate<CommendaAiMessageFeedbackResponse>(
    conversationPath(conversationId, `/messages/${messageId}/feedback`),
    { method: 'POST', body: verdict },
    'Feedback failed'
  );
}

export async function sendAgentConversationMessage(
  conversationId: string,
  body: Readonly<SendAgentConversationMessageBody>,
  signal?: AbortSignal
): Promise<CommendaAiMutationResult<AgentConversationSendAcceptedView>> {
  return AgentApiRequestHelper.mutate<AgentConversationSendAcceptedView>(
    conversationPath(conversationId, '/messages'),
    { method: 'POST', body, ...(signal ? { signal } : {}) },
    'The message could not be sent.'
  );
}

export async function streamAgentConversationMessage(
  conversationId: string,
  body: Readonly<SendAgentConversationMessageBody>,
  signal?: AbortSignal
): Promise<CommendaAiMutationResult<ReadableStream<Uint8Array>>> {
  return AgentApiRequestHelper.stream(
    conversationPath(conversationId, '/messages/stream'),
    { method: 'POST', body, ...(signal ? { signal } : {}) },
    'The message could not be sent.'
  );
}

export async function stopAgentConversation(
  conversationId: string
): Promise<CommendaAiMutationResult> {
  return AgentApiRequestHelper.mutate(
    conversationPath(conversationId, '/stop'),
    { method: 'POST' },
    'Could not stop the reply.'
  );
}

/** Envelope rows → AI SDK messages. The envelope resolves every stored dialect into `parts` server
 * side, so unlike the chat-only hydrator there is no content fallback to apply here beyond an
 * empty turn still in flight, which must keep its place in the list. */
export function hydrateEnvelopeMessages(
  envelope: AgentConversationEnvelope
): UIMessage[] {
  return envelope.messages.map((message): UIMessage => {
    // senderName only when the read carried a sender join. Without one the avatar falls back the
    // same way it does for a turn sent in this session and not yet stored.
    const senderName = message.sender?.name;
    const metadata: CommendaAiMessageMetadata = {
      createdAt: message.createdAt,
      timelineBlocks: message.timelineBlocks ?? null,
      ...(typeof senderName === 'string' ? { senderName } : {}),
    };

    return {
      id: message.messageId,
      role: message.role === 'USER' ? 'user' : 'assistant',
      parts: message.parts ?? [],
      metadata,
    };
  });
}
